import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useApi, post, put, Panel, PageHead, Stat, QuickForm, DataTable, StatusBadge, Tabs, inr,
} from "@/crm/ui";
import { ArrowLeft, Plus, Building2 } from "lucide-react";

const BANK_FIELDS = [
  { key: "bank_name", label: "Bank Name" }, { key: "bank_code", label: "Bank Code" },
  { key: "short_name", label: "Short Name" },
  { key: "bank_type", label: "Bank Type", options: ["Bank", "NBFC", "Fintech", "Other"] },
  { key: "bank_category", label: "Category", options: ["Private Bank", "Public Sector Bank", "Small Finance Bank", "NBFC", "Housing Finance", "Other"] },
  { key: "head_office", label: "Head Office" }, { key: "website", label: "Website" },
  { key: "priority", label: "Priority", options: ["High", "Medium", "Low"] },
  { key: "remarks", label: "Remarks" },
];

const LOC_FIELDS = [
  { key: "location_name", label: "Location Name" }, { key: "branch_name", label: "Branch Name" },
  { key: "branch_code", label: "Branch Code" }, { key: "city", label: "City" },
  { key: "district", label: "District" }, { key: "state", label: "State" },
  { key: "pincode", label: "Pincode" }, { key: "address", label: "Full Address" },
  { key: "location_type", label: "Location Type", options: ["Branch", "Regional Office", "Processing Centre", "Credit Office", "Sales Office", "Other"] },
];

const MGR_FIELDS = (locations) => [
  { key: "manager_name", label: "Manager Name" }, { key: "designation", label: "Designation" },
  { key: "contact_type", label: "Contact Type", options: ["Sales Manager", "Branch Manager", "Credit Manager", "Credit Analyst", "Operations Manager", "Login Team", "Disbursement Team", "Regional Manager", "Zonal Manager", "Head Office Contact", "Other"] },
  { key: "location_id", label: "Location (optional for bank-level)", options: locations.map((l) => ({ value: l.location_id, label: l.location_name })) },
  { key: "email", label: "Official Email" }, { key: "mobile", label: "Official Mobile" },
  { key: "department", label: "Department" }, { key: "staff_code", label: "Staff Code" },
];

export default function Banks() {
  const [selected, setSelected] = useState(null);
  const [locationId, setLocationId] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const banks = useApi("/crm/banks");
  const profile = useApi(selected ? `/crm/banks/${selected}` : null, [selected]);
  const products = useApi("/crm/products");
  const bankAudit = useApi(selected && tab === "Audit History" ? `/crm/audit?entity_id=${selected}` : null,
                           [selected, tab]);
  const bankReport = useApi(selected && tab === "Reports" ? `/crm/reports/location?bank_id=${selected}` : null,
                            [selected, tab]);
  const locationDetail = useApi(locationId ? `/crm/locations/${locationId}` : null, [locationId]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const createBank = async () => {
    const res = await post("/crm/banks", form, "Bank created");
    if (res) {
      setForm({}); setShowCreate(false); banks.reload();
      setSelected(res.bank_id); setTab("Locations");
    }
  };

  if (locationId && locationDetail.data) {
    const { location, managers: locManagers, products: locProducts, applications: locApps, performance } =
      locationDetail.data;
    return (
      <div className="space-y-6" data-testid="location-detail-page">
        <Button variant="ghost" onClick={() => { setLocationId(null); setForm({}); }}
                data-testid="back-to-bank-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to {location.bank_name}
        </Button>
        <PageHead eyebrow={`${location.bank_name} · ${location.location_id}`} title={location.location_name}
                  subtitle={`${location.branch_name || "—"} · ${location.city || "—"}, ${location.state || "—"} · ${location.location_type}`}
                  actions={<StatusBadge value={location.status} />} />
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
          <Stat label="Managers" value={locManagers.length} testid="ld-managers" />
          <Stat label="Products" value={locProducts.length} testid="ld-products" />
          <Stat label="Applications" value={performance.applications} testid="ld-apps" />
          <Stat label="Login Amount" value={inr(performance.login_amount)} testid="ld-login" />
          <Stat label="Disbursed" value={inr(performance.disbursed_amount)} testid="ld-disbursed" />
        </div>
        <Panel title="Location & bank information" testid="location-overview-panel">
          <div className="grid gap-3 text-sm sm:grid-cols-3">
            {[["Location ID", location.location_id], ["Bank", `${location.bank_name} (${location.bank_id})`],
              ["Branch Name", location.branch_name], ["Branch Code", location.branch_code],
              ["City", location.city], ["District", location.district], ["State", location.state],
              ["Pincode", location.pincode], ["Address", location.address],
              ["Type", location.location_type], ["Created By", location.created_by],
              ["Remarks", location.remarks]].map(([k, v]) => (
              <p key={k}><span className="text-muted-foreground">{k}: </span><b>{v || "—"}</b></p>))}
          </div>
        </Panel>
        <Panel title="+ Add Manager at this location" testid="location-add-manager-panel"
               aside={<span className="text-xs text-muted-foreground">Bank &amp; location are locked</span>}>
          <QuickForm testid="location-manager-form" values={form} submitLabel="Add Manager"
                     onChange={set}
                     fields={MGR_FIELDS([]).filter((f) => f.key !== "location_id")}
                     onSubmit={async () => {
                       const r = await post(`/crm/banks/${location.bank_id}/managers`,
                         { ...form, location_id: location.location_id }, "Manager added");
                       if (r) { setForm({}); locationDetail.reload(); }
                     }} />
        </Panel>
        <Panel title={`Managers (${locManagers.length})`} testid="location-managers-panel">
          <DataTable testid="location-managers-table" rows={locManagers}
                     columns={[{ key: "manager_id", label: "Manager ID" },
                               { key: "manager_name", label: "Name" },
                               { key: "contact_type", label: "Contact Type" },
                               { key: "mobile", label: "Mobile" }, { key: "email", label: "Email" },
                               { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
        </Panel>
        <Panel title={`Products available here (${locProducts.length})`} testid="location-products-panel">
          <DataTable testid="location-products-table" rows={locProducts}
                     columns={[{ key: "product_name", label: "Product" },
                               { key: "location_name", label: "Scope", render: (r) => r.location_name || "All locations" },
                               { key: "min_amount", label: "Min", right: true, render: (r) => inr(r.min_amount) },
                               { key: "max_amount", label: "Max", right: true, render: (r) => inr(r.max_amount) },
                               { key: "tenure", label: "Tenure" }, { key: "rate_range", label: "Rate" },
                               { key: "tat", label: "TAT" }]} />
        </Panel>
        <Panel title={`Applications (${locApps.length})`} testid="location-applications-panel">
          <DataTable testid="location-applications-table" rows={locApps}
                     columns={[{ key: "application_id", label: "Application" },
                               { key: "customer_name", label: "Customer" },
                               { key: "product_name", label: "Product" },
                               { key: "login_amount", label: "Login", right: true, render: (r) => inr(r.login_amount) },
                               { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                               { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
        </Panel>
      </div>
    );
  }

  if (selected && profile.data) {
    const { bank, summary, locations, managers, products: mapped, applications } = profile.data;
    const reload = () => profile.reload();
    return (
      <div className="space-y-6" data-testid="bank-profile-page">
        <Button variant="ghost" onClick={() => { setSelected(null); banks.reload(); }} data-testid="back-to-banks-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> All banks
        </Button>
        <PageHead eyebrow={`Bank Profile · ${bank.bank_id}`} title={bank.bank_name}
                  subtitle={`${bank.bank_type} · ${bank.bank_category} · ${bank.head_office || "—"}`}
                  actions={<>
                    <StatusBadge value={bank.status} />
                    <Button variant="outline" className="bg-white"
                            onClick={() => put(`/crm/banks/${bank.bank_id}`,
                              { status: bank.status === "Active" ? "Inactive" : "Active" },
                              "Bank status updated").then(reload)}
                            data-testid="toggle-bank-status-btn">
                      {bank.status === "Active" ? "Deactivate" : "Activate"}
                    </Button>
                  </>} />
        <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
          <Stat label="Locations" value={summary.locations} testid="bp-locations" />
          <Stat label="Managers" value={summary.managers} testid="bp-managers" />
          <Stat label="Products" value={summary.products} testid="bp-products" />
          <Stat label="Login Count" value={summary.login_count} testid="bp-login-count" />
          <Stat label="Approval Count" value={summary.approval_count} testid="bp-approval-count" />
          <Stat label="Active Applications" value={summary.active_applications} testid="bp-apps" />
          <Stat label="Login Amount" value={inr(summary.login_amount)} testid="bp-login" />
          <Stat label="Approved Amount" value={inr(summary.approved_amount)} testid="bp-approved" />
          <Stat label="Disbursement Amount" value={inr(summary.disbursed_amount)} testid="bp-disbursed" />
        </div>
        <Tabs tabs={["Overview", "Locations", "Managers", "Products", "Applications", "Performance",
                     "Reports", "Audit History"]}
              active={tab} onChange={setTab} testid="bank-tabs" />

        {tab === "Overview" && (
          <Panel title="Bank information" testid="bank-overview">
            <div className="grid gap-3 text-sm sm:grid-cols-3">
              {[["Bank ID", bank.bank_id], ["Bank Code", bank.bank_code], ["Short Name", bank.short_name],
                ["Type", bank.bank_type], ["Category", bank.bank_category], ["Priority", bank.priority],
                ["Head Office", bank.head_office], ["Website", bank.website],
                ["Created By", bank.created_by], ["Created", (bank.created_at || "").slice(0, 10)],
                ["Updated By", bank.updated_by], ["Remarks", bank.remarks]].map(([k, v]) => (
                <p key={k}><span className="text-muted-foreground">{k}: </span><b>{v || "—"}</b></p>
              ))}
            </div>
          </Panel>
        )}

        {tab === "Locations" && (
          <>
            <Panel title="+ Add Location" testid="add-location-panel"
                   aside={<span className="text-xs text-muted-foreground">Bank: {bank.bank_name} (locked)</span>}>
              <QuickForm fields={LOC_FIELDS} values={form} onChange={set} testid="location-form"
                         submitLabel="Add Location"
                         onSubmit={async () => {
                           const r = await post(`/crm/banks/${bank.bank_id}/locations`, form, "Location added");
                           if (r) { setForm({}); reload(); }
                         }} />
            </Panel>
            <Panel title={`Locations (${locations.length})`} testid="locations-panel"
                   aside={<span className="text-xs text-muted-foreground">Open a location to manage its managers and products</span>}>
              <DataTable testid="locations-table" rows={locations} onRowClick={(r) => { setLocationId(r.location_id); setForm({}); }}
                         columns={[{ key: "location_id", label: "Location ID" },
                                   { key: "location_name", label: "Location" },
                                   { key: "branch_code", label: "Branch Code" },
                                   { key: "city", label: "City" }, { key: "state", label: "State" },
                                   { key: "location_type", label: "Type" },
                                   { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                                   { key: "open", label: "", render: (r) => (
                                     <Button size="sm" variant="outline" className="bg-white text-[10px]"
                                             data-testid={`open-location-${r.location_id}`}
                                             onClick={(e) => { e.stopPropagation(); setLocationId(r.location_id); setForm({}); }}>
                                       Open</Button>) }]} />
            </Panel>
          </>
        )}

        {tab === "Managers" && (
          <>
            <Panel title="+ Add Manager / Contact" testid="add-manager-panel"
                   aside={<span className="text-xs text-muted-foreground">Leave location empty for a bank-level contact</span>}>
              <QuickForm fields={MGR_FIELDS(locations)} values={form} onChange={set} testid="manager-form"
                         submitLabel="Add Manager"
                         onSubmit={async () => {
                           const r = await post(`/crm/banks/${bank.bank_id}/managers`, form, "Manager added");
                           if (r) { setForm({}); reload(); }
                         }} />
            </Panel>
            <Panel title={`Managers (${managers.length})`} testid="managers-panel">
              <DataTable testid="managers-table" rows={managers}
                         columns={[{ key: "manager_id", label: "Manager ID" },
                                   { key: "manager_name", label: "Name" },
                                   { key: "contact_type", label: "Contact Type" },
                                   { key: "location_name", label: "Location", render: (r) => r.location_name || "Bank level" },
                                   { key: "mobile", label: "Mobile" }, { key: "email", label: "Email" },
                                   { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
            </Panel>
          </>
        )}

        {tab === "Products" && (
          <>
            <Panel title="+ Map Product" testid="map-product-panel">
              <QuickForm testid="map-product-form" values={form} onChange={set} submitLabel="Map Product"
                         fields={[
                           { key: "product_id", label: "Product", options: (products.data || []).map((p) => ({ value: p.product_id, label: p.product_name })) },
                           { key: "location_id", label: "Location (optional)", options: locations.map((l) => ({ value: l.location_id, label: l.location_name })) },
                           { key: "min_amount", label: "Min Amount", type: "number" },
                           { key: "max_amount", label: "Max Amount", type: "number" },
                           { key: "tenure", label: "Tenure" }, { key: "rate_range", label: "Rate Range" },
                           { key: "processing_fee", label: "Processing Fee" }, { key: "tat", label: "TAT" },
                         ]}
                         onSubmit={async () => {
                           const r = await post(`/crm/banks/${bank.bank_id}/products`, form, "Product mapped");
                           if (r) { setForm({}); reload(); }
                         }} />
            </Panel>
            <Panel title={`Mapped products (${mapped.length})`} testid="bank-products-panel">
              <DataTable testid="bank-products-table" rows={mapped}
                         columns={[{ key: "product_name", label: "Product" },
                                   { key: "location_name", label: "Location", render: (r) => r.location_name || "All locations" },
                                   { key: "min_amount", label: "Min", right: true, render: (r) => inr(r.min_amount) },
                                   { key: "max_amount", label: "Max", right: true, render: (r) => inr(r.max_amount) },
                                   { key: "tenure", label: "Tenure" }, { key: "rate_range", label: "Rate" },
                                   { key: "tat", label: "TAT" }]} />
            </Panel>
          </>
        )}

        {(tab === "Applications" || tab === "Performance") && (
          <Panel title={tab === "Applications" ? `Applications (${applications.length})` : "Performance"}
                 testid="bank-applications-panel">
            <DataTable testid="bank-applications-table" rows={applications}
                       columns={[{ key: "application_id", label: "Application" },
                                 { key: "customer_name", label: "Customer" },
                                 { key: "location_name", label: "Location" },
                                 { key: "product_name", label: "Product" },
                                 { key: "login_amount", label: "Login", right: true, render: (r) => inr(r.login_amount) },
                                 { key: "approved_amount", label: "Approved", right: true, render: (r) => inr(r.approved_amount) },
                                 { key: "disbursed_amount", label: "Disbursed", right: true, render: (r) => inr(r.disbursed_amount) },
                                 { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> }]} />
          </Panel>
        )}
        {tab === "Reports" && (
          <Panel title="Location-wise performance" testid="bank-reports-panel">
            <DataTable testid="bank-reports-table" rows={bankReport.data?.rows}
                       empty="No applications logged for this bank yet."
                       columns={[{ key: "label", label: "Location" },
                                 { key: "login", label: "Login", right: true },
                                 { key: "login_amount", label: "Login Amt", right: true, render: (r) => inr(r.login_amount) },
                                 { key: "approved", label: "Approved", right: true },
                                 { key: "approved_amount", label: "Approved Amt", right: true, render: (r) => inr(r.approved_amount) },
                                 { key: "hold", label: "Hold", right: true },
                                 { key: "rejected", label: "Rejected", right: true },
                                 { key: "disbursed_amount", label: "Disbursed Amt", right: true, render: (r) => inr(r.disbursed_amount) },
                                 { key: "approval_rate", label: "Approval %", right: true },
                                 { key: "average_tat", label: "Avg TAT", right: true }]} />
          </Panel>
        )}

        {tab === "Audit History" && (
          <Panel title={`Audit history (${bankAudit.data?.total ?? 0})`} testid="bank-audit-panel">
            <DataTable testid="bank-audit-table" rows={bankAudit.data?.records}
                       empty="No audit records yet."
                       columns={[{ key: "created_at", label: "Date / Time", render: (r) => (r.created_at || "").replace("T", " ").slice(0, 19) },
                                 { key: "user_email", label: "User" },
                                 { key: "action_type", label: "Action" },
                                 { key: "entity", label: "Entity" },
                                 { key: "field", label: "Field" },
                                 { key: "previous_value", label: "Old Value" },
                                 { key: "new_value", label: "New Value" },
                                 { key: "reason", label: "Reason" }]} />
          </Panel>
        )}
      </div>
    );
  }

  const rows = (banks.data || []).filter((b) =>
    !q || `${b.bank_name} ${b.bank_id} ${b.bank_code}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6" data-testid="banks-page">
      <PageHead eyebrow="Masters" title="Banks"
                subtitle="A bank is the parent entity — locations and managers can only be created from inside a bank profile."
                actions={<Button className="rounded-full" onClick={() => setShowCreate((s) => !s)}
                                 data-testid="create-bank-btn"><Plus className="mr-2 h-4 w-4" /> Create Bank</Button>} />
      {showCreate && (
        <Panel title="Create Bank" testid="create-bank-panel">
          <QuickForm fields={BANK_FIELDS} values={form} onChange={set} onSubmit={createBank}
                     submitLabel="Create Bank" testid="bank-form" />
        </Panel>
      )}
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search banks…"
             className="max-w-xs bg-white" data-testid="bank-search-input" />
      <Panel testid="banks-list-panel">
        <DataTable testid="banks-table" rows={rows} onRowClick={(r) => { setSelected(r.bank_id); setTab("Overview"); setForm({}); }}
                   empty="No banks yet — create your first bank."
                   columns={[{ key: "bank_id", label: "Bank ID" }, { key: "bank_code", label: "Code" },
                             { key: "bank_name", label: "Bank Name", render: (r) => (
                               <span className="flex items-center gap-2"><Building2 className="h-3 w-3 text-primary" />{r.bank_name}</span>) },
                             { key: "bank_type", label: "Type" }, { key: "bank_category", label: "Category" },
                             { key: "locations", label: "Locations", right: true },
                             { key: "managers", label: "Managers", right: true },
                             { key: "products", label: "Products", right: true },
                             { key: "active_applications", label: "Active Apps", right: true },
                             { key: "total_disbursement", label: "Disbursed", right: true, render: (r) => inr(r.total_disbursement) },
                             { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
                             { key: "manage", label: "Action", render: (r) => (
                               <Button size="sm" variant="outline" className="bg-white"
                                       data-testid={`manage-bank-${r.bank_id}`}
                                       onClick={(e) => { e.stopPropagation(); setSelected(r.bank_id); setTab("Overview"); }}>
                                 Manage</Button>) }]} />
      </Panel>
    </div>
  );
}
